/**
 * "Advertiser pages" — every advertiser hub with its passcode, child-page
 * counts, views and sync state. Row actions are delegated to the tab;
 * revoke/restore and delete sit in the row's ⋯ menu.
 */

import React from 'react';
import { Button, PinCell, RowActionsMenu, Spinner, buttonStyles, tableStyles } from '../../ui';
import type { AdvertiserReportPage, HubCampaignCounts } from '../../../services/advertiserReportsApi';
import { isSyncLive, plural, sectionSubtitleStyle, sectionTitleStyle } from './shared';
import { SyncPill } from './pills';

interface AdvertiserPagesTableProps {
  pages: AdvertiserReportPage[];
  loading: boolean;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  onSync: (page: AdvertiserReportPage) => void;
  onManage: (page: AdvertiserReportPage) => void;
  onResetPin: (page: AdvertiserReportPage) => void;
  onToggleRevoke: (page: AdvertiserReportPage) => void;
  onDelete: (page: AdvertiserReportPage) => void;
}

const COUNT_PARTS: Array<[keyof HubCampaignCounts, string]> = [
  ['live', 'live'],
  ['upcoming', 'upcoming'],
  ['paused', 'paused'],
  ['completed', 'completed'],
];

const CampaignCounts: React.FC<{ counts?: HubCampaignCounts }> = ({ counts }) => {
  if (!counts) return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  const parts = COUNT_PARTS.filter(([k]) => counts[k] > 0).map(([k, word]) => `${counts[k]} ${word}`);
  return (
    <>
      <span style={{ whiteSpace: 'nowrap' }}>{parts.length > 0 ? parts.join(' · ') : '0 campaigns'}</span>
      {counts.generating > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: 2 }}>{counts.generating} generating</div>
      )}
    </>
  );
};

const AdvertiserPagesTable: React.FC<AdvertiserPagesTableProps> = ({
  pages,
  loading,
  copiedKey,
  onCopy,
  onSync,
  onManage,
  onResetPin,
  onToggleRevoke,
  onDelete,
}) => {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={sectionTitleStyle}>Advertiser pages</div>
        <div style={sectionSubtitleStyle}>
          One passcode per advertiser opens the hub and every campaign report on it. New campaigns are discovered hourly;
          delivery data and device breakdowns refresh hourly, with reach refreshed daily or when you click Sync now.
        </div>
      </div>

      {loading && pages.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spinner />
        </div>
      ) : pages.length === 0 ? (
        <div
          style={{
            border: '1px dashed var(--border-1)',
            borderRadius: 'var(--radius-lg)',
            padding: '18px 14px',
            fontSize: 12.5,
            color: 'var(--fg-4)',
            textAlign: 'center',
          }}
        >
          No advertiser pages yet — use “Create advertiser page” on an advertiser above to group its campaigns behind one passcode.
        </div>
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Client / Advertiser</th>
                <th style={tableStyles.th}>PIN</th>
                <th style={tableStyles.th}>Campaigns</th>
                <th style={tableStyles.thNum}>Views</th>
                <th style={tableStyles.th}>Sync</th>
                <th style={tableStyles.th} />
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => {
                const aliases = Math.max(0, (p.gamAdvertiserIds?.length ?? 1) - 1);
                const syncing = isSyncLive(p);
                return (
                  <tr key={p.slug}>
                    <td style={{ ...tableStyles.td, fontWeight: 600 }}>
                      {p.clientLabel}
                      <div style={{ fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 }}>
                        {p.advertiserName !== p.clientLabel && <>{p.advertiserName} · </>}
                        {aliases > 0 && <>+{plural(aliases, 'alias', 'aliases')} · </>}
                        {p.includeOrderIds?.length > 0 && <>{plural(p.includeOrderIds.length, 'manual include')} · </>}
                        /advertisers/{p.slug}/reports
                      </div>
                    </td>
                    <td style={tableStyles.td}>
                      <PinCell
                        label={p.clientLabel}
                        pin={p.pin}
                        copied={copiedKey === `hubpin:${p.slug}`}
                        onCopy={() => onCopy(`hubpin:${p.slug}`, p.pin)}
                        onReset={() => onResetPin(p)}
                      />
                    </td>
                    <td style={tableStyles.td}>
                      <CampaignCounts counts={p.campaignCounts} />
                    </td>
                    <td style={tableStyles.tdNum}>{p.accessCount}</td>
                    <td style={tableStyles.td}>
                      <SyncPill page={p} />
                    </td>
                    <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          style={buttonStyles('subtle', 'sm')}
                          aria-label={`Open ${p.clientLabel} (new tab)`}
                        >
                          Open ↗
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => onCopy(`hublink:${p.slug}`, `${p.url}\nPasscode: ${p.pin}`)}>
                          {copiedKey === `hublink:${p.slug}` ? 'Copied ✓' : 'Copy link + PIN'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={syncing || !!p.revokedAt}
                          onClick={() => onSync(p)}
                          title={p.revokedAt ? 'Restore the page first' : 'Discover new campaigns, link/create their pages and generate reports now'}
                        >
                          {syncing ? 'Syncing…' : 'Sync now'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onManage(p)} aria-label={`Manage ${p.clientLabel}`}>
                          Manage
                        </Button>
                        <RowActionsMenu
                          label={p.clientLabel}
                          actions={[
                            { label: p.revokedAt ? 'Restore' : 'Revoke…', onSelect: () => onToggleRevoke(p) },
                            { label: 'Delete…', danger: true, onSelect: () => onDelete(p) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdvertiserPagesTable;
